/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import { useEffect, useState } from "react";
import ESSuccessView from "./partials/ESSuccessView";
import { PAMediaBatch } from "../mediaRemoval/mediaRemoval.types";
import { postAutoclaveServices } from "../../../../service/postAutoclaveServices";
import {
	AutoclaveEquipmentsDetail,
	ContainerForSterialization,
	ContainersSterilizationListResponse,
	SterilizationParameters,
} from "./emptySterilization.types";
import ESHeader from "./partials/ESHeader";
import MediaPrepTable from "../../../common/commonComponents/lotDetailsTable";
import { getColumnsForContainerSterilization } from "./partials/columns";
import ESConstraintMessage from "./partials/ESConstraintMessage";
import SterilizationCycleParameters from "./partials/SterilizationCycleParameters";
import ESFooter from "./partials/ESFooter";
import ESConfirmationModal from "./partials/ESConfirmationModal";
import CustomToast from "../../../../widgets/toaster";

const EmptySterilization = () => {
	const { t } = useTranslation();

	const [showSuccess, setShowSuccess] = useState(false);
	const [mediaBatches, setMediaBatches] = useState<PAMediaBatch[]>([]);
	const [selectedBatch, setSelectedBatch] = useState<PAMediaBatch>();
	const [successCount, setSuccessCount] = useState(0);
	const [searchQuery, setSearchQuery] = useState("");
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [autoclaveEquipments, setAutoclaveEquipments] = useState<
		AutoclaveEquipmentsDetail[]
	>([]);
	const [refreshTrigger, setRefreshTrigger] = useState(false);
	const [containers, setContainers] = useState<ContainerForSterialization[]>(
		[],
	);
	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});

	const [errors, setErrors] = useState({
		batch: "",
		autoclave: "",
		cycleNumber: "",
		temperature: "",
		pressure: "",
		startDate: "",
		endDate: "",
		table: "",
	});

	const validate = () => {
		const newErrors = {
			batch: "",
			autoclave: "",
			cycleNumber: "",
			temperature: "",
			pressure: "",
			startDate: "",
			endDate: "",
			table: "",
		};

		let isValid = true;

		if (!selectedBatch) {
			newErrors.batch = t("postAutoclaveProcess.errors.batch");
			isValid = false;
		}

		if (containers.length === 0) {
			newErrors.table = t("postAutoclaveProcess.errors.plates");
			isValid = false;
		}

		if (!sterilizationParameters.autoclave_id) {
			newErrors.autoclave = t("postAutoclaveProcess.errors.autoclave");
			isValid = false;
		}

		if (!sterilizationParameters.cycle_number.trim()) {
			newErrors.cycleNumber = t("postAutoclaveProcess.errors.cycleNumber");
			isValid = false;
		}

		if (!sterilizationParameters.temperature_celsius) {
			newErrors.temperature = t("postAutoclaveProcess.errors.temperature");
			isValid = false;
		}

		if (!sterilizationParameters.pressure_bar) {
			newErrors.pressure = t("postAutoclaveProcess.errors.pressure");
			isValid = false;
		}

		if (!sterilizationParameters.cycle_start_datetime) {
			newErrors.startDate = t("postAutoclaveProcess.errors.startDate");
			isValid = false;
		}

		if (!sterilizationParameters.cycle_end_datetime) {
			newErrors.endDate = t("postAutoclaveProcess.errors.endDate");
			isValid = false;
		}

		if (
			sterilizationParameters.cycle_start_datetime &&
			sterilizationParameters.cycle_end_datetime
		) {
			const start = new Date(
				sterilizationParameters.cycle_start_datetime
			).getTime();

			const end = new Date(
				sterilizationParameters.cycle_end_datetime
			).getTime();

			if (end <= start) {
				newErrors.endDate = t(
					"postAutoclaveProcess.errors.invalidEndDate"
				);
				isValid = false;
			}
		}

		setErrors(newErrors);
		return isValid;
	};

	const handleConfirm = () => {
		if (!validate()) return;

		setShowConfirmationModal(true);
	};

	const [sterilizationParameters, setSterilizationParameters] =
		useState<SterilizationParameters>({
			media_batch_id: "",
			autoclave_id: "",
			cycle_number: "",
			temperature_celsius: "",
			pressure_bar: "",
			duration_min: "",
			cycle_start_datetime: "",
			cycle_end_datetime: "",
		});
	const [stats, setStats] = useState({
		total: 0,
		eligible: 0,
		rejected: 0,
	});

	const getMediaBatches = async () => {
		const urlParams = {};
		try {
			const response =
				await postAutoclaveServices.getMediaBatches(urlParams);
			setMediaBatches(response.data);
			setSelectedBatch(undefined);
		} catch (error) {
			console.log(error);
		}
	};

	const getContainers = async () => {
		if (!selectedBatch) return;
		const urlParams = {
			sort: "id",
			order: "desc",
			media_batch_id: selectedBatch.id,
			keyword: searchQuery,
		};
		try {
			const response: ContainersSterilizationListResponse =
				await postAutoclaveServices.getContainersForSterilization(
					urlParams,
				);
			setContainers(response.data);
			setStats({
				total: response.totalElements,
				eligible: response.totalEligible,
				rejected: response.totalRejected,
			});
		} catch (error) {
			console.log(error);
		}
	};

	const getAutoclaveEquipments = async () => {
		try {
			const response = await postAutoclaveServices.getAutoclaveEquipments();
			setAutoclaveEquipments(response.data);
		} catch (error) {
			console.log(error);
		}
	};

	const handleSuccess = () => {
		setSuccessCount(stats.eligible);
		setShowSuccess(true);
		setSterilizationParameters({
			media_batch_id: "",
			autoclave_id: "",
			cycle_number: "",
			temperature_celsius: "",
			pressure_bar: "",
			duration_min: "",
			cycle_start_datetime: "",
			cycle_end_datetime: "",
		});
		setRefreshTrigger(!refreshTrigger);
	};

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	useEffect(() => {
		getMediaBatches();
		getAutoclaveEquipments();
	}, []);

	useEffect(() => {
		getContainers();
	}, [selectedBatch, refreshTrigger, searchQuery]);
	
	useEffect(() => {
	if (selectedBatch) {
		setSterilizationParameters((prev) => ({
		...prev,
		media_batch_id: selectedBatch.id,
		}));
	}
	}, [selectedBatch]);

	if (showSuccess) {
		return (
			<ESSuccessView
				goBack={() => setShowSuccess(false)}
				batchInfo={{ totalPlatesEligible: successCount }}
			/>
		);
	}
	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("postAutoclaveProcess.title")} pageDescription={t('pageDescription.postAutoclave.description')}/>
			<div className="flex flex-col h-full bg-[#F8FAFC] relative overflow-hidden">
				<ESHeader
					stats={stats}
					batchInfo={selectedBatch}
					selectedBatch={selectedBatch}
					setSelectedBatch={(batch) => {
						setSelectedBatch(batch);

						setErrors(prev => ({
							...prev,
							batch: "",
						}));
					}}
					mediaBatches={mediaBatches}
					errors={errors}
					setErrors={setErrors}
				/>
				<div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
					<div className="grid grid-cols-12 gap-3">
						<div className="col-span-12 lg:col-span-5 space-y-6">
							<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[450px]">
								<div className="px-8 py-2 border-b border-slate-100 bg-slate-50/50 flex flex-col gap-4">
									<div className="flex items-center gap-3">
										<span className="material-symbols-outlined text-orange-500 text-xl">
											list_alt
										</span>
										<h3 className="text-xs font-black text-[#1E293B] uppercase tracking-[0.15em] mt-2">
											{t(
												"postAutoclaveProcess.emptySterilization.platesElgibleForSterilization",
											)}
										</h3>
									</div>
									<div className="relative group">
										<span className="material-symbols-outlined absolute left-4 top-1/4 -translate-y-1/2 text-slate-400 group-focus-within:text-orange-500 transition-colors">
											qr_code_scanner
										</span>
										<input
											type="text"
											placeholder={t(
												"postAutoclaveProcess.emptySterilization.searchPlaceholder",
											)}
											value={searchQuery}
											onChange={(e) =>
												setSearchQuery(e.target.value)
											}
											className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium outline-none focus:ring-4 focus:ring-orange-50 focus:border-orange-500 transition-all"
										/>
									</div>
								</div>
								<MediaPrepTable
									columns={getColumnsForContainerSterilization(t)}
									data={containers}
									emptyMessage="No Plates"
								/>
								{errors.table && (
									<div className="px-8 py-2">
										<p className="text-[11px] text-red-500">
											{errors.table}
										</p>
									</div>
								)}
								<div className="mt-auto px-8 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
									<p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
										{t(
											"postAutoclaveProcess.emptySterilization.totalPlatesForSterilization",
										)}
									</p>
									<p className="text-xl font-black text-orange-600">
										{stats.eligible}
									</p>
								</div>
							</div>
							<ESConstraintMessage />
						</div>
						<div className="col-span-12 lg:col-span-7 space-y-8">
							<SterilizationCycleParameters
								sterilizationParameters={sterilizationParameters}
								setSterilizationParameters={setSterilizationParameters}
								autoclaveEquipments={autoclaveEquipments}
								errors={errors}
								setErrors={setErrors}
							/>
						</div>
					</div>
					<ESFooter
						toggleConfirmationModal={handleConfirm}
						sterilizationParameters={sterilizationParameters}
						totalContainers={containers.length}
					/>
					{showConfirmationModal && (
						<ESConfirmationModal
							containers={containers}
							mediaBatchId={selectedBatch?.id ?? ""}
							eligiblePlates={stats.eligible}
							sterilizationParameters={sterilizationParameters}
							toggleConfirmationModal={() =>
								setShowConfirmationModal(false)
							}
							showToast={showToast}
							handleSuccess={handleSuccess}
						/>
					)}
				</div>
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={400}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight" // optional
			/>
		</div>
	);
};

export default EmptySterilization;
